import { streamRepository } from "../storage/storage";
import { v4 as uuidv4 } from "uuid";
import Stream from "../sports/common/stream";
import StretchliveApiService from "./stretchlive-api-service";
import StreamProvider from "../sports/common/StreamProvider";

const providerMap = {
    [StreamProvider.StretchLive]: StretchliveApiService
};

class StreamService {
    async getStreams(eventId) {
        return (
            await streamRepository.where({ eventId: eventId }).toArray()
        ).sort((a, b) => a.index - b.index);
    }

    async saveStream(stream) {
        const streamCount = await streamRepository
            .where({ eventId: stream.eventId })
            .count();
        stream.id = await streamRepository.put(
            Object.assign(new Stream(), stream, {
                index: stream.index || streamCount + 1
            })
        );
        return stream;
    }

    async getStream(id) {
        return streamRepository.getById(id);
    }

    async deleteStreams(eventId) {
        const streams = await this.getStreams(eventId);
        await streamRepository.bulkDelete(streams.map((s) => s.id));
    }

    async deleteStream(id) {
        const stream = await this.getStream(id);
        if (stream) {
            streamRepository.delete(id);
        }
    }

    async saveStreamMarker(streamId, marker) {
        const stream = await this.getStream(streamId);

        if (stream) {
            await streamRepository.put(
                Object.assign(stream, {
                    markers: [
                        ...stream.markers.filter((m) => m.id !== marker.id),
                        { ...marker, id: marker.id || uuidv4() }
                    ]
                })
            );
        }

        return stream;
    }

    async deleteStreamMarker(streamId, markerId) {
        const stream = await this.getStream(streamId);

        if (stream) {
            await streamRepository.put(
                Object.assign(stream, {
                    markers: stream.markers.filter((m) => m.id !== markerId)
                })
            );
        }

        return stream;
    }

    async getProviderSrc(stream) {
        try {
            const src = await providerMap[stream.providerCode].getStreamSrcById(
                stream.providerId
            );
            return this.saveStream(
                Object.assign(stream, { src, isProviderSrcStale: false })
            );
        } catch (e) {
            console.error(
                "Provider source could not be found:",
                stream.providerId
            );
        }
    }

    /**
     * Update playId on markers for streams corresponding to the provided eventId
     * @param {string} eventId
     * @param {string} playId
     * @param {string | null} updatedPlayId
     * @returns {Promise<void>}
     */
    async updateMarkerPlayIdRefs(eventId, playId, updatedPlayId) {
        try {
            const streams = await streamRepository
                .where({ eventId: eventId })
                .toArray();

            await streamRepository.bulkPut(
                streams
                    .filter(({ markers }) =>
                        markers.some((m) => m.playId === playId)
                    )
                    .map((s) =>
                        Object.assign(s, {
                            markers: s.markers.map((m) => {
                                if (m.playId === playId) {
                                    return {
                                        ...m,
                                        playId: updatedPlayId
                                    };
                                }
                                return m;
                            })
                        })
                    )
            );
        } catch (e) {
            console.error(e);
        }
    }
}

export default new StreamService();
